import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useTenant } from '@/contexts/TenantContext';
import { useQuality } from '@/contexts/QualityContext';
import { useTheme } from '@/contexts/ThemeContext';
import CRMSidebar from '@/modules/CRM/components/CRMSidebar';
import QualitySidebar from '@/modules/QMS/components/QualitySidebar';
// SettingsSidebar replaced by ManagerSidebar (rewritten for /settings/ routes)
import { AnalyticsSidebar, LMSSidebar, ManagerSidebar, HRSidebar, FinanceSidebar, DeveloperSidebar } from './ModuleSidebars';
import SupportPanel from '@/components/SupportPanel';
import AgentMenu from '@/components/AgentMenu';
import SystemLogic from '@/modules/Analytics/components/SystemLogic';


const ThemeCalendarIcon = ({ size = 20, isActive = false }) => {
    const date = new Date();
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const dayNum = date.getDate();

    return (
        <div style={{
            width: size,
            height: size,
            background: 'transparent',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
            transition: 'all 0.2s ease',
            flexShrink: 0
        }}>
            <div style={{
                background: isActive ? 'var(--accent)' : 'var(--bg-muted)',
                color: isActive ? 'white' : 'var(--text-tertiary)',
                fontSize: `${size * 0.25}px`,
                fontWeight: '500',
                height: '35%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.05em'
            }}>
                {dayName}
            </div>
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                background: 'var(--bg-surface)',
                fontSize: `${size * 0.45}px`,
                fontWeight: '500'
            }}>
                {dayNum}
            </div>
        </div>
    );
};

export default function Sidebar({ mobileOpen = false, isCollapsed, isHidden = false, isHovered = false, onToggle, onToggleCollapse, onHide, onHoverStart, onHoverEnd }) {
    const location = useLocation();
    const { userRole, user, actualRole, accountLevel, switchAccountLevel, isActualPrivileged } = useAuth();
    const { currentTenant } = useTenant();
    const { currentCompany } = useCompany();
    const { auditCounts } = useQuality();
    const navigate = useNavigate();
    const [supportOpen, setSupportOpen] = useState(false);
    const [showLogic, setShowLogic] = useState(false);
    const [favorites, setFavorites] = useState([]);

    const activeOrgName = (currentTenant?.name && currentTenant?.name !== 'Primary Workspace' && currentTenant?.name !== 'Primary Organization') 
        ? currentTenant.name.replace(' (Fallback)', '') 
        : (currentCompany?.name || 'The Scale Agency');

    const isLMS = location.pathname.startsWith('/lms');

    // LMS Role switcher state (Trainee View vs Trainer Hub)
    const [lmsRole, setLmsRole] = useState(() => {
        return localStorage.getItem('oblique_lms_role') || 'trainee';
    });

    const updateLmsRole = (newRole) => {
        setLmsRole(newRole);
        localStorage.setItem('oblique_lms_role', newRole);
        window.dispatchEvent(new CustomEvent('oblique_lms_role_changed', { detail: { role: newRole } }));
    };

    useEffect(() => {
        const handleRoleChange = (e) => {
            if (e.detail?.role && e.detail.role !== lmsRole) {
                setLmsRole(e.detail.role);
            }
        };
        window.addEventListener('oblique_lms_role_changed', handleRoleChange);
        return () => window.removeEventListener('oblique_lms_role_changed', handleRoleChange);
    }, [lmsRole]);

    // Account Level Configurations (Context-aware: Trainer/Trainee in LMS vs Manager/User elsewhere)
    const ACCOUNT_LEVELS = isLMS ? [
        { 
            id: 'superadmin', 
            label: 'Superadmin', 
            icon: 'security', 
            badgeColor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
            desc: 'Master System Access'
        },
        { 
            id: 'admin', 
            label: 'Admin', 
            icon: 'admin_panel_settings', 
            badgeColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
            desc: 'Academy Administration'
        },
        { 
            id: 'manager', 
            label: 'Trainer', 
            icon: 'supervisor_account', 
            badgeColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
            desc: 'Cohort & Assessment Oversight'
        },
        { 
            id: 'user', 
            label: 'Trainee', 
            icon: 'school', 
            badgeColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
            desc: 'Curriculum & Assessments'
        }
    ] : [
        { 
            id: 'superadmin', 
            label: 'Superadmin', 
            icon: 'security', 
            badgeColor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
            desc: 'Master System Access'
        },
        { 
            id: 'admin', 
            label: 'Admin', 
            icon: 'admin_panel_settings', 
            badgeColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
            desc: 'Full Organization Control'
        },
        { 
            id: 'manager', 
            label: 'Manager', 
            icon: 'manage_accounts', 
            badgeColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
            desc: 'Team & Quality Oversight'
        },
        { 
            id: 'user', 
            label: 'User', 
            icon: 'person', 
            badgeColor: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
            desc: 'Standard Member Access'
        }
    ];

    const rawLevel = (accountLevel || userRole || 'superadmin').toLowerCase();
    const currentLevelId = isLMS
        ? (rawLevel === 'user' || rawLevel === 'trainee' || (!accountLevel && lmsRole === 'trainee') ? 'user' :
           rawLevel === 'manager' || rawLevel === 'trainer' || (!accountLevel && lmsRole === 'trainer') ? 'manager' :
           rawLevel)
        : (rawLevel === 'trainer' ? 'manager' : rawLevel === 'trainee' ? 'user' : rawLevel);

    const currentLevel = ACCOUNT_LEVELS.find(lvl => lvl.id === currentLevelId) || ACCOUNT_LEVELS[0];
    const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
    const accountDropdownRef = React.useRef(null);

    const canSwitchPerspective = isActualPrivileged || actualRole === 'superadmin' || actualRole === 'admin' || user?.email?.toLowerCase().includes('sanjay') || !userRole;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target)) {
                setIsAccountDropdownOpen(false);
            }
        };
        if (isAccountDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isAccountDropdownOpen]);

    useEffect(() => {
        const loadFavorites = () => {
            setFavorites(JSON.parse(localStorage.getItem('oblique_favorites') || '[]'));
        };
        loadFavorites();
        window.addEventListener('oblique-favorites-updated', loadFavorites);
        return () => window.removeEventListener('oblique-favorites-updated', loadFavorites);
    }, []);

    const isQuality = location.pathname.startsWith('/quality') || location.pathname.startsWith('/qms');
    const isCRM = location.pathname === '/' || location.pathname.startsWith('/operations') || location.pathname.startsWith('/crm') || location.pathname.startsWith('/automations');
    const isAnalytics = location.pathname.startsWith('/analytics');
    const isSettingsModule = location.pathname.startsWith('/settings') || location.pathname.startsWith('/admin');
    const isHR = location.pathname.startsWith('/hr');
    const isFinance = location.pathname.startsWith('/finance');
    const isDeveloper = location.pathname.startsWith('/developer');

    // In hidden mode: sidebar is 0-width normally, but overlays on hover
    const showAsOverlay = isHidden && isHovered;

    const sidebarStyle = {
        position: 'fixed',
        top: 0,
        left: isHidden && !isHovered ? '-260px' : 0,
        bottom: 0,
        height: '100vh',
        width: showAsOverlay ? '260px' : (isHidden ? '0px' : (isCollapsed ? '64px' : '260px')),
        background: 'var(--bg-surface)',
        color: 'var(--text-secondary)',
        display: 'flex',
        flexDirection: 'column',
        borderRight: (isHidden && !isHovered) ? 'none' : '1px solid var(--border)',
        transition: 'width 150ms ease-out, left 150ms ease-out',
        zIndex: 'var(--z-sidebar)',
        overflow: 'hidden',
        boxShadow: showAsOverlay ? '8px 0 32px rgba(0,0,0,0.12)' : 'none'
    };

    const navStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        marginTop: '12px',
        padding: '0 8px'
    };

    const getNavItemStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        gap: '10px',
        padding: isCollapsed ? '6px 0' : '6px 10px',
        borderRadius: '6px',
        transition: 'all 0.15s ease',
        textDecoration: 'none',
        fontSize: '13.5px',
        fontWeight: isActive ? '700' : '500',
        background: 'transparent',
        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
        width: '100%',
        minHeight: '32px',
        position: 'relative'
    });

    const badgeStyle = (count) => {
        if (!count || isCollapsed) return null;
        return (
            <span style={{ 
                marginLeft: 'auto', 
                fontSize: '10px', 
                fontWeight: '600', 
                color: 'var(--text-tertiary)',
                background: 'var(--bg-subtle)',
                padding: '2px 6px',
                borderRadius: '4px'
            }}>
                {count}
            </span>
        );
    };

    return (
        <>
            {/* Hover trigger zone when sidebar is hidden */}
            {isHidden && !isHovered && (
                <div
                    onMouseEnter={onHoverStart}
                    style={{
                        position: 'fixed', left: 0, top: 0, bottom: 0,
                        width: '6px', zIndex: 'var(--z-sidebar-backdrop)',
                        background: 'transparent'
                    }}
                />
            )}
            {/* Backdrop overlay when sidebar is showing as hover overlay */}
            {showAsOverlay && (
                <div
                    onClick={onHoverEnd}
                    onMouseEnter={onHoverEnd}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.08)',
                        zIndex: 'var(--z-sidebar-backdrop)',
                        transition: 'opacity 0.2s'
                    }}
                />
            )}
            <aside 
                className={`desktop-sidebar tour-step-sidebar ${mobileOpen ? 'mobile-open' : ''}`}
                style={sidebarStyle} 
                onMouseEnter={isHidden ? onHoverStart : undefined}
                onMouseLeave={isHidden ? onHoverEnd : undefined}
            >
                {/* Aligned 54px header row matching navbar height and bottom border */}
                <div className="h-[54px] min-h-[54px] max-h-[54px] box-border border-b border-[var(--border)] flex items-center justify-between px-4 shrink-0 transition-colors">
                    {!isCollapsed ? (
                        <>
                            <Link to="/" className="flex items-center" title="Oblique Dashboard">
                                <Logo size={110} iconOnly={false} />
                            </Link>
                            {isLMS && (
                                <button
                                    onClick={onToggle}
                                    title="Collapse sidebar"
                                    className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors border-0 bg-transparent cursor-pointer"
                                >
                                    <ObliqueIcon name="chevron_left" size={16} />
                                </button>
                            )}
                        </>
                    ) : (
                        <Link to="/" className="flex items-center justify-center w-full" title="Oblique Dashboard">
                            <Logo size={26} iconOnly={true} />
                        </Link>
                    )}
                </div>

            <nav style={navStyle} className="custom-scrollbar pt-2">
                {!isCollapsed && favorites.length > 0 && (
                    <div className="mb-4">
                        <div className="px-3 pb-2">
                            <span className="text-[10px] font-semibold tracking-wider text-[var(--text-tertiary)] uppercase">
                                Favorites
                            </span>
                        </div>
                        {favorites.map((fav, i) => (
                            <button
                                key={i}
                                onClick={() => navigate(fav.path)}
                                style={getNavItemStyle(fav.path)}
                            >
                                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                    <ObliqueIcon name="description" size={16} color="var(--text-tertiary)" />
                                    <span className="truncate">{fav.title}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {isCRM && <CRMSidebar isCollapsed={isCollapsed} getNavItemStyle={getNavItemStyle} ThemeCalendarIcon={ThemeCalendarIcon} />}
                {isQuality && <QualitySidebar 
                    isCollapsed={isCollapsed} 
                    getNavItemStyle={getNavItemStyle} 
                    badgeStyle={badgeStyle} 
                    auditCounts={auditCounts} 
                    ThemeCalendarIcon={ThemeCalendarIcon} 
                />}
                {isAnalytics && <AnalyticsSidebar isCollapsed={isCollapsed} getNavItemStyle={getNavItemStyle} userRole={userRole} />}
                {isLMS && <LMSSidebar isCollapsed={isCollapsed} getNavItemStyle={getNavItemStyle} ThemeCalendarIcon={ThemeCalendarIcon} lmsRole={lmsRole} />}
                {isSettingsModule && <ManagerSidebar isCollapsed={isCollapsed} getNavItemStyle={getNavItemStyle} userRole={userRole} location={location} />}
                {isHR && <HRSidebar isCollapsed={isCollapsed} location={location} />}
                {isFinance && <FinanceSidebar isCollapsed={isCollapsed} location={location} />}
                {isDeveloper && <DeveloperSidebar isCollapsed={isCollapsed} location={location} />}

            </nav>

            {/* Bottom Account Level & View Switcher (Discreet quiet trigger) */}
            <div className="mt-auto border-t border-[var(--border)] px-3 py-2.5 flex items-center justify-between gap-2 shrink-0">
                {!isCollapsed ? (
                    <>
                        <div className="relative flex-1 min-w-0" ref={accountDropdownRef}>
                            <div
                                onClick={() => {
                                    if (canSwitchPerspective) {
                                        setIsAccountDropdownOpen(prev => !prev);
                                    }
                                }}
                                className="flex items-center gap-2 py-0.5 select-none cursor-default group"
                                title={currentLevel.label}
                            >
                                <div className="text-[var(--text-tertiary)] flex items-center justify-center shrink-0">
                                    <ObliqueIcon name={currentLevel.icon} size={15} />
                                </div>
                                <span className="text-[12px] font-medium text-[var(--text-tertiary)] truncate leading-none">
                                    {currentLevel.label}
                                </span>
                            </div>

                            {/* Dropdown Menu Popup (Opens upwards, auto-stretches cleanly) */}
                            <AnimatePresence>
                                {isAccountDropdownOpen && canSwitchPerspective && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 6, scale: 0.96 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 6, scale: 0.96 }}
                                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                                        className="absolute bottom-full left-0 mb-2 w-52 p-1 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-subtle)] shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-50 overflow-hidden"
                                    >
                                        <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--border-subtle)] mb-1 flex items-center justify-between">
                                            <span>Simulate View</span>
                                            <span className="text-[10px] text-[var(--text-tertiary)] font-normal lowercase tracking-normal">preview</span>
                                        </div>

                                        <div className="flex flex-col gap-0.5">
                                            {ACCOUNT_LEVELS.map(lvl => {
                                                const isSelected = lvl.id === currentLevelId;
                                                return (
                                                    <button
                                                        key={lvl.id}
                                                        type="button"
                                                        onClick={() => {
                                                            switchAccountLevel(lvl.id);
                                                            if (lvl.id === 'user') updateLmsRole('trainee');
                                                            else updateLmsRole('trainer');
                                                            setIsAccountDropdownOpen(false);
                                                        }}
                                                        className={`w-full px-2 py-1.5 rounded-md flex items-center justify-between text-left transition-colors duration-150 cursor-pointer border-0 bg-transparent ${
                                                            isSelected 
                                                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-medium' 
                                                                : 'hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="flex items-center justify-center shrink-0 w-4 h-4">
                                                                <ObliqueIcon 
                                                                    name={lvl.icon} 
                                                                    size={15} 
                                                                    color={isSelected ? "var(--accent)" : "var(--text-secondary)"} 
                                                                />
                                                            </div>
                                                            <span className="text-[13px] truncate">{lvl.label}</span>
                                                        </div>
                                                        {isSelected && <ObliqueIcon name="check" size={14} className="text-[var(--accent)] shrink-0" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <button 
                            onClick={onToggleCollapse} 
                            className="flex items-center justify-center w-7 h-7 rounded-lg bg-transparent border-0 cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0"
                            title="Collapse sidebar"
                        >
                            <ObliqueIcon name="chevron_left" size={16} />
                        </button>
                    </>
                ) : (
                    <div className="flex flex-col items-center gap-2 w-full relative" ref={accountDropdownRef}>
                        <div
                            onClick={() => {
                                if (canSwitchPerspective) {
                                    setIsAccountDropdownOpen(prev => !prev);
                                }
                            }}
                            title={currentLevel.label}
                            className="flex items-center justify-center w-8 h-8 select-none text-[var(--text-tertiary)] cursor-default"
                        >
                            <ObliqueIcon name={currentLevel.icon} size={16} />
                        </div>

                        {/* Dropdown Menu Popup in collapsed mode (Opens upwards and floats rightwards) */}
                        <AnimatePresence>
                            {isAccountDropdownOpen && canSwitchPerspective && (
                                <motion.div
                                    initial={{ opacity: 0, x: -6, scale: 0.96 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: -6, scale: 0.96 }}
                                    transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute bottom-full left-0 mb-2 w-52 p-1 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-subtle)] shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-50 overflow-hidden"
                                >
                                    <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--border-subtle)] mb-1 flex items-center justify-between">
                                        <span>Simulate View</span>
                                        <span className="text-[10px] text-[var(--text-tertiary)] font-normal lowercase tracking-normal">preview</span>
                                    </div>

                                    <div className="flex flex-col gap-0.5">
                                        {ACCOUNT_LEVELS.map(lvl => {
                                            const isSelected = lvl.id === currentLevelId;
                                            return (
                                                <button
                                                    key={lvl.id}
                                                    type="button"
                                                    onClick={() => {
                                                        switchAccountLevel(lvl.id);
                                                        if (lvl.id === 'user') updateLmsRole('trainee');
                                                        else updateLmsRole('trainer');
                                                        setIsAccountDropdownOpen(false);
                                                    }}
                                                    className={`w-full px-2 py-1.5 rounded-md flex items-center justify-between text-left transition-colors duration-150 cursor-pointer border-0 bg-transparent ${
                                                        isSelected 
                                                            ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-medium' 
                                                            : 'hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className="flex items-center justify-center shrink-0 w-4 h-4">
                                                            <ObliqueIcon 
                                                                name={lvl.icon} 
                                                                size={15} 
                                                                color={isSelected ? "var(--accent)" : "var(--text-secondary)"} 
                                                            />
                                                        </div>
                                                        <span className="text-[13px] truncate">{lvl.label}</span>
                                                    </div>
                                                    {isSelected && <ObliqueIcon name="check" size={14} className="text-[var(--accent)] shrink-0" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <button 
                            onClick={onToggleCollapse} 
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-transparent border-0 cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0"
                            title="Expand sidebar"
                        >
                            <ObliqueIcon name="chevron_right" size={16} />
                        </button>
                    </div>
                )}
            </div>
        </aside>
        <SupportPanel isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
        {showLogic && <SystemLogic onClose={() => setShowLogic(false)} isSuperAdmin={userRole === 'superadmin' || user?.role === 'superadmin'} />}
        </>
    );
}
